/**
 * Warm-up emails are the first one-time campaigns a new customer sends. Staff author them as
 * Suggested Drafts in Customer Management and mark them ONLY by name, using the convention
 * "{Org}- Warm Up Email N", sometimes followed by a proposed send date
 * ("{Org}- Warm Up Email 1- Sept 15, 2026"). There is deliberately no `warmUp` attribute.
 *
 * KEEP IN SYNC: this file is duplicated in
 *   - backend/customer-management-api/src/shared/utils/warm-up.js
 *   - backend/campaigns-api/src/shared/warm-up.js (the original; re-anchoring runs there)
 * Each copy has a test pinning the same example names, so a drift shows up as a failing test.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// Send warm-up #1 this long after the customer finishes onboarding (time to launch it).
const WARM_UP_LEAD_DAYS = 2;
// Gap between consecutive warm-ups when the drafts' original send times give no usable gap.
const WARM_UP_FALLBACK_GAP_DAYS = 3;

// "\b" on both sides: "Email 12" is 12 (not 1), and anything after the number is ignored.
const WARM_UP_NAME = /\bwarm[\s-]?up\s+email\s*#?\s*(\d+)\b/i;

const MONTH = '(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?';
const DATE_LIKE = new RegExp(
  [
    `${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{2,4})?`, // Sept 15, 2026 / September 15
    '\\d{1,2}/\\d{1,2}(?:/\\d{2,4})?', // 9/15/26 / 9/15
    '\\d{4}-\\d{2}-\\d{2}', // 2026-09-15
  ].join('|'),
  'i',
);
// The text after "Email N": an optional separator, then exactly one date and nothing else.
const DATE_SUFFIX = new RegExp(`^\\s*[-–—:|,]?\\s*(?:${DATE_LIKE.source})\\s*$`, 'i');

/** The warm-up number from a campaign/draft name, or null when it is not a warm-up. */
const parseWarmUpOrder = (name) => {
  const match = typeof name === 'string' ? name.match(WARM_UP_NAME) : null;
  return match ? Number(match[1]) : null;
};

const isWarmUpName = (name) => parseWarmUpOrder(name) !== null;

/**
 * Drops a trailing proposed-send-date suffix ("- Sept 15, 2026") from a warm-up name, so the name
 * does not contradict the re-anchored send date. Anything after "Email N" that is not exactly a
 * recognisable date ("- final", " copy") is left alone, as is any non-warm-up name.
 */
const stripWarmUpDateSuffix = (name) => {
  if (typeof name !== 'string') return name;
  const match = name.match(WARM_UP_NAME);
  if (!match) return name;
  const end = match.index + match[0].length;
  const suffix = name.slice(end);
  return suffix && DATE_SUFFIX.test(suffix) ? name.slice(0, end).trim() : name;
};

const toMs = (value) => {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Orders warm-ups by the number in their name; equal numbers (a staff naming mistake) fall back to
 * the original send time so the result is still deterministic.
 */
const sortWarmUps = (campaigns) =>
  campaigns
    .map((campaign) => ({ campaign, order: parseWarmUpOrder(campaign.name) }))
    .filter(({ order }) => order !== null)
    .sort((a, b) => a.order - b.order || (toMs(a.campaign.sendTime) ?? 0) - (toMs(b.campaign.sendTime) ?? 0))
    .map(({ campaign, order }) => ({ ...campaign, warmUpOrder: order }));

/**
 * Re-anchors warm-up send times to when the customer finished onboarding.
 *
 * - The first warm-up moves to `onboardedAt + leadDays`, keeping its original (UTC) time of day,
 *   pushed one more day when that time has already passed on the target day — so it is never
 *   sooner than the lead time.
 * - Each later warm-up keeps its original gap from the previous one; a missing or non-positive
 *   gap becomes `fallbackGapDays`.
 *
 * Pure: takes campaigns (any status — the caller filters) and returns
 * `[{ campaignId, warmUpOrder, sendTime, name }]` in send order. `name` has its date suffix stripped.
 */
const computeWarmUpSchedule = (
  campaigns,
  onboardedAtMs,
  { leadDays = WARM_UP_LEAD_DAYS, fallbackGapDays = WARM_UP_FALLBACK_GAP_DAYS } = {},
) => {
  const ordered = sortWarmUps(campaigns);
  const earliest = onboardedAtMs + leadDays * DAY_MS;

  let previousOriginal = null;
  let previousTarget = null;

  return ordered.map((campaign) => {
    const original = toMs(campaign.sendTime);
    let target;

    if (previousTarget === null) {
      if (original === null) {
        target = earliest;
      } else {
        const originalDate = new Date(original);
        const candidate = new Date(earliest);
        candidate.setUTCHours(
          originalDate.getUTCHours(),
          originalDate.getUTCMinutes(),
          originalDate.getUTCSeconds(),
          originalDate.getUTCMilliseconds(),
        );
        target = candidate.getTime() < earliest ? candidate.getTime() + DAY_MS : candidate.getTime();
      }
    } else {
      const gap = original !== null && previousOriginal !== null ? original - previousOriginal : NaN;
      target = previousTarget + (gap > 0 ? gap : fallbackGapDays * DAY_MS);
    }

    // A missing original makes the NEXT gap unknown, so it gets the fallback gap.
    previousOriginal = original;
    previousTarget = target;

    return {
      campaignId: campaign.campaignId,
      warmUpOrder: campaign.warmUpOrder,
      sendTime: new Date(target).toISOString(),
      name: stripWarmUpDateSuffix(campaign.name),
    };
  });
};

/**
 * Dashboard-only: "when does this send" copy for the onboarding Warm-up section. Dates are not shown
 * because they are re-anchored when onboarding finishes; the gaps come from the same schedule math.
 * Returns `{ [campaignId]: label }`.
 */
const getWarmUpTimingLabels = (campaigns) => {
  const schedule = computeWarmUpSchedule(campaigns, 0);
  return schedule.reduce((labels, item, index) => {
    if (index === 0) {
      labels[item.campaignId] = `Sends ~${WARM_UP_LEAD_DAYS} days after you finish onboarding`;
    } else {
      const previous = schedule[index - 1];
      const days = Math.max(1, Math.round((Date.parse(item.sendTime) - Date.parse(previous.sendTime)) / DAY_MS));
      labels[item.campaignId] = `~${days} day${days === 1 ? '' : 's'} after Warm-up Email ${previous.warmUpOrder}`;
    }
    return labels;
  }, {});
};

/**
 * Dashboard-only: the warm-ups the onboarding step shows, in send order. Only ones the tenant can
 * still edit (suggested/draft, not launched); a launched or sent warm-up lives on the Campaigns page.
 */
const selectEditableWarmUps = (campaigns = []) =>
  sortWarmUps(
    campaigns.filter(
      (campaign) =>
        isWarmUpName(campaign.name) && ['suggested', 'draft'].includes(campaign.sendStatus) && !campaign.launched,
    ),
  );

/**
 * Dashboard-only: how many editable warm-ups still need review. Reviewing = saving once, which
 * accepts the suggested campaign (suggested -> draft). Shared by the onboarding step's Next gate
 * and useOnboarding's step resolution so the two can't disagree.
 */
const countWarmUpsPendingReview = (warmUps = []) =>
  warmUps.filter((campaign) => campaign.sendStatus === 'suggested').length;

export {
  getWarmUpTimingLabels,
  selectEditableWarmUps,
  countWarmUpsPendingReview,
  WARM_UP_LEAD_DAYS,
  WARM_UP_FALLBACK_GAP_DAYS,
  parseWarmUpOrder,
  isWarmUpName,
  stripWarmUpDateSuffix,
  sortWarmUps,
  computeWarmUpSchedule,
};
